import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { WORKFLOW_CHAIN, statusLabel } from '../orders/workflowConstants';
import { formatInr } from './dashboardPeriodUtils';

const ALL_STATUS_KEYS = WORKFLOW_CHAIN.map((w) => w.key);

const STATUS_COLORS = {
  DRAFT: '#64748b',
  APPROVED: '#3b82f6',
  PLACE_ORDER: '#eab308',
  IN_PRODUCTION: '#14b8a6',
  PRODUCTION_DONE: '#06b6d4',
  BUTTA_CUTTING: '#a16207',
  MILL_FINISH: '#f97316',
  BORDER_CUTTING: '#ec4899',
  HANDWORK: '#78716c',
  READY_FOR_DISPATCH: '#22c55e',
  IN_STOCK: '#0ea5e9',
  DISPATCHED: '#10b981',
  DELIVERED: '#7c3aed',
  CANCELLED: '#ef4444',
};

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Flatten nested maps — Recharts tooltip often loses nested objects on payload. */
function toChartRows(buckets) {
  return (buckets || []).map((b) => {
    const ordersByStatus = b.ordersByStatus || {};
    const piecesByStatus = b.piecesByStatus || {};
    const amountByStatus = b.amountByStatus || {};

    const row = {
      label: b.label,
      bucketKey: b.bucketKey,
      totalAmount: num(b.totalAmount),
      totalOrders: num(b.totalOrders),
      totalPieces: num(b.totalPieces),
    };

    for (const key of ALL_STATUS_KEYS) {
      row[key] = num(ordersByStatus[key]);
      row[`pieces_${key}`] = num(piecesByStatus[key]);
      row[`amount_${key}`] = num(amountByStatus[key]);
    }
    return row;
  });
}

function pickHoveredSegment(payload) {
  if (!payload?.length) return null;
  const barHits = payload.filter(
    (p) => p.dataKey && p.dataKey !== 'totalAmount' && num(p.value) > 0
  );
  if (barHits.length === 1) return barHits[0];
  if (barHits.length > 1) {
    const active = barHits.find((p) => p.active === true);
    if (active) return active;
    return barHits[barHits.length - 1];
  }
  const line = payload.find((p) => p.dataKey === 'totalAmount');
  return line || payload[0];
}

function SegmentTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const seg = pickHoveredSegment(payload);
  if (!seg) return null;

  const dataKey = seg.dataKey;
  const row = seg.payload || {};

  if (dataKey === 'totalAmount') {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-lg text-sm">
        <p className="font-semibold text-slate-900">{label}</p>
        <p className="text-emerald-700 font-semibold mt-1">Total amount: {formatInr(seg.value)}</p>
        <p className="text-slate-600 text-xs mt-1">{num(row.totalOrders)} orders</p>
      </div>
    );
  }

  const orders = num(seg.value);
  const pieces = num(row[`pieces_${dataKey}`]);
  const amount = num(row[`amount_${dataKey}`]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-lg text-sm min-w-[140px]">
      <p className="font-semibold text-slate-900">{statusLabel(dataKey)}</p>
      <p className="text-slate-500 text-xs mt-0.5">{label}</p>
      <dl className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-600">Orders</dt>
          <dd className="font-semibold tabular-nums">{orders}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-600">Pieces</dt>
          <dd className="font-semibold tabular-nums">{pieces.toLocaleString()}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-600">Amount</dt>
          <dd className="font-semibold tabular-nums text-emerald-700">{formatInr(amount)}</dd>
        </div>
      </dl>
    </div>
  );
}

function ChartStatusLegend() {
  return (
    <div className="mt-3 pt-3 border-t border-slate-200">
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 justify-center max-h-[120px] overflow-y-auto px-1">
        {WORKFLOW_CHAIN.map((w) => (
          <span
            key={w.key}
            className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] text-slate-700 whitespace-nowrap"
          >
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0 border border-black/5"
              style={{ backgroundColor: STATUS_COLORS[w.key] || '#94a3b8' }}
              aria-hidden
            />
            {w.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] text-slate-700 whitespace-nowrap">
          <span className="w-4 h-0.5 bg-emerald-600 rounded shrink-0" aria-hidden />
          Total amount
        </span>
      </div>
    </div>
  );
}

export function OrdersByTimeChart({ analytics, loading }) {
  const chartData = useMemo(() => toChartRows(analytics?.chartBuckets), [analytics?.chartBuckets]);

  const yMax = useMemo(() => {
    let max = 0;
    for (const row of chartData) {
      let stack = 0;
      for (const key of ALL_STATUS_KEYS) {
        stack += num(row[key]);
      }
      if (stack > max) max = stack;
    }
    const step = max <= 20 ? 5 : max <= 50 ? 10 : 20;
    return Math.max(step, Math.ceil(max / step) * step);
  }, [chartData]);

  if (loading) {
    return <div className="h-[400px] rounded-xl bg-slate-100 animate-pulse" />;
  }

  const allZero =
    chartData.length > 0 &&
    chartData.every((row) => num(row.totalOrders) === 0 && num(row.totalAmount) === 0);

  if (!chartData.length || allZero) {
    return (
      <div className="h-[200px] rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-sm text-slate-500">
        No orders in this period
      </div>
    );
  }

  const isHourly = analytics?.bucketType === 'hour';
  const dense = !isHourly && chartData.length > 12;
  const hourTickInterval = isHourly ? 1 : 0;

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-2 sm:p-4">
      <ResponsiveContainer width="100%" height={dense ? 420 : 380}>
        <ComposedChart
          data={chartData}
          margin={{ top: 16, right: 56, left: 8, bottom: dense ? 56 : 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: isHourly ? 9 : 11, fill: '#475569' }}
            interval={isHourly ? hourTickInterval : dense ? Math.ceil(chartData.length / 12) : 0}
            angle={dense ? -40 : isHourly ? -45 : 0}
            textAnchor={dense || isHourly ? 'end' : 'middle'}
            height={dense || isHourly ? 56 : 40}
            minTickGap={isHourly ? 4 : 8}
          />
          <YAxis
            yAxisId="orders"
            allowDecimals={false}
            domain={[0, yMax]}
            tick={{ fontSize: 11, fill: '#475569' }}
            width={40}
            label={{
              value: 'Orders',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { fontSize: 11, fill: '#64748b', fontWeight: 600 },
            }}
          />
          <YAxis
            yAxisId="amount"
            orientation="right"
            tickFormatter={(v) => formatInr(v)}
            tick={{ fontSize: 10, fill: '#475569' }}
            width={56}
            label={{
              value: 'Total amount',
              angle: 90,
              position: 'insideRight',
              offset: 10,
              style: { fontSize: 11, fill: '#64748b', fontWeight: 600 },
            }}
          />
          <Tooltip
            shared={false}
            isAnimationActive={false}
            content={<SegmentTooltip />}
            cursor={{ fill: 'rgba(148,163,184,0.12)' }}
          />
          {ALL_STATUS_KEYS.map((key) => (
            <Bar
              key={key}
              yAxisId="orders"
              dataKey={key}
              name={key}
              stackId="orders"
              fill={STATUS_COLORS[key] || '#94a3b8'}
              maxBarSize={64}
              isAnimationActive={false}
            />
          ))}
          <Line
            yAxisId="amount"
            type="monotone"
            dataKey="totalAmount"
            name="totalAmount"
            stroke="#16a34a"
            strokeWidth={2.5}
            dot={{ r: 4, fill: '#16a34a', stroke: '#fff', strokeWidth: 1 }}
            activeDot={{ r: 6 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartStatusLegend />
    </div>
  );
}

export default OrdersByTimeChart;
